import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'

const API_URL = process.env.REACT_APP_API_URL

const UserRegister = () => {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const [temporaryPassword, setTemporaryPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [isVerifying, setIsVerifying] = useState(true)
    const [errorMessage, setErrorMessage] = useState('')
    const [token, setToken] = useState('')
    const [email, setEmail] = useState('')
    const [userInfo, setUserInfo] = useState(null)
    const [isTokenValid, setIsTokenValid] = useState(false)
    const [passwordRulesVisible, setPasswordRulesVisible] = useState(false)
    const passwordInputRef = useRef(null)
    const rulesRef = useRef(null)

    useEffect(() => {
        // Extract token and email from URL query parameters
        const tokenParam = searchParams.get('token')
        const emailParam = searchParams.get('email')

        if (!tokenParam || !emailParam) {
            setErrorMessage('Invalid invitation link. Please check your email for the correct link.')
            // toast.error('Invalid invitation link. Missing token or email.', { autoClose: 3000 })
            setIsVerifying(false)
        } else {
            setToken(tokenParam)
            // Decode email if it's URL encoded
            const decodedEmail = decodeURIComponent(emailParam)
            setEmail(decodedEmail)
            // Verify token
            verifyInviteToken(tokenParam, decodedEmail)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams])

    const verifyInviteToken = async (tokenValue, emailValue) => {
        setIsVerifying(true)
        setErrorMessage('')

        try {
            const payload = {
                action: 'verify-invite-token',
                token: tokenValue,
                email: emailValue
            }

            const response = await axios.post(API_URL, payload)

            // Parse the response - handle both string and object responses
            let responseData = response.data
            if (typeof responseData === 'string') {
                try {
                    responseData = JSON.parse(responseData)
                } catch (parseError) {
                    responseData = response.data
                }
            }

            // Check if token is valid
            if (responseData.success === true && responseData.user) {
                setIsTokenValid(true)
                setUserInfo(responseData.user)
            } else {
                setIsTokenValid(false)
                setErrorMessage(responseData.message || 'Invalid or expired invitation token')
                // toast.error(responseData.message || 'Invalid or expired invitation token', { autoClose: 3000 })
            }
        } catch (error) {
            console.error('Verify token error:', error)
            setIsTokenValid(false)
            let errorMsg = 'Failed to verify invitation token'
            if (error.response && error.response.data) {
                let errorData = error.response.data
                if (typeof errorData === 'string') {
                    try {
                        errorData = JSON.parse(errorData)
                    } catch (e) {
                        // Use error message as is
                    }
                }
                errorMsg = errorData.message || errorMsg
            }
            setErrorMessage(errorMsg)
            // toast.error(errorMsg, { autoClose: 3000 })
        } finally {
            setIsVerifying(false)
        }
    }

    const validatePassword = (password) => {
        // Password rules: 8 chars, uppercase, lowercase, number, special char
        const minLength = password.length >= 8
        const hasUpperCase = /[A-Z]/.test(password)
        const hasLowerCase = /[a-z]/.test(password)
        const hasNumber = /\d/.test(password)
        const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)

        return {
            isValid: minLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar,
            errors: {
                minLength: !minLength,
                hasUpperCase: !hasUpperCase,
                hasLowerCase: !hasLowerCase,
                hasNumber: !hasNumber,
                hasSpecialChar: !hasSpecialChar
            }
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setErrorMessage('')

        // Validation
        if (!temporaryPassword.trim()) {
            setErrorMessage('Please enter the temporary password')
            return
        }

        if (!newPassword.trim()) {
            setErrorMessage('Please enter a new password')
            return
        }

        if (!confirmPassword.trim()) {
            setErrorMessage('Please confirm your password')
            return
        }

        // Check password match
        if (newPassword !== confirmPassword) {
            setErrorMessage('Passwords do not match')
            return
        }

        // Validate password rules
        const passwordValidation = validatePassword(newPassword)
        if (!passwordValidation.isValid) {
            setErrorMessage('Password does not meet the requirements')
            return
        }

        if (!token || !email) {
            setErrorMessage('Invalid invitation link. Please check your email.')
            return
        }

        if (!isTokenValid) {
            setErrorMessage('Invalid or expired invitation token')
            return
        }

        setIsLoading(true)

        try {
            const payload = {
                action: 'accept-invite',
                token: token,
                email: email,
                temporaryPassword: temporaryPassword.trim(),
                newPassword: newPassword
            }

            const response = await axios.post(API_URL, payload)

            // Parse the response - handle both string and object responses
            let responseData = response.data
            if (typeof responseData === 'string') {
                try {
                    responseData = JSON.parse(responseData)
                } catch (parseError) {
                    responseData = response.data
                }
            }

            // Check if API call was successful
            if (responseData.success === true) {
                toast.success('Invitation accepted successfully.', { autoClose: 3000 })
                // Navigate to login page after a short delay
                setTimeout(() => {
                    navigate('/')
                }, 2000)
            } else {
                setErrorMessage(responseData.message || 'Failed to accept invitation')
                // toast.error(responseData.message || 'Failed to accept invitation', { autoClose: 2000 })
            }
        } catch (error) {
            console.error('Accept invite error:', error)
            let errorMsg = 'An error occurred while accepting invitation'
            if (error.response && error.response.data) {
                let errorData = error.response.data
                if (typeof errorData === 'string') {
                    try {
                        errorData = JSON.parse(errorData)
                    } catch (e) {
                        // Use error message as is
                    }
                }
                errorMsg = errorData.message || errorMsg
            }
            setErrorMessage(errorMsg)
            // toast.error(errorMsg, { autoClose: 2000 })
        } finally {
            setIsLoading(false)
        }
    }

    const passwordValidation = newPassword ? validatePassword(newPassword) : null

    // Check if password is valid
    const isPasswordValid = (pass) => {
        if (!pass) return false
        const validation = validatePassword(pass)
        return validation.isValid
    }

    return (
        <div className="hold-transition login-page accent-custom">
            <div className="login-box login_div">
                <div className="card card-outline card-primary">
                    <div className="card-header text-center">
                        <div className="login_logo">
                            <img src='/assets/img/color icon_circle.png' border="0" alt="Logo" />
                        </div>
                    </div>
                    <div className="card-body d-flex flex-row flex_flow_rowwrap">
                        <div className="col-12 col-sm-12 col-md-12">
                            <h5 className="login-box-msg mb-2">Accept Invitation</h5>
                            {isVerifying ? (
                                <div className="text-center py-4">
                                    <PulseLoader color="#0094a9" size={10} />
                                    <p className="mt-3 text-muted">Verifying invitation...</p>
                                </div>
                            ) : isTokenValid && userInfo ? (
                                <>
                                    <p className="text-center text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                                        Welcome, <strong>{userInfo.name || email}</strong>
                                    </p>
                                    <form onSubmit={handleSubmit}>
                                        <div className="input-group mb-3">
                                            <input
                                                type="password"
                                                className="form-control"
                                                placeholder="Temporary Password"
                                                value={temporaryPassword}
                                                onChange={(e) => {
                                                    setTemporaryPassword(e.target.value)
                                                    setErrorMessage('')
                                                }}
                                                required
                                                disabled={isLoading}
                                            />
                                            <div className="input-group-append">
                                                <div className="input-group-text">
                                                    <i className="fas fa-key" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="input-group mb-3">
                                            <input
                                                ref={passwordInputRef}
                                                type="password"
                                                className="form-control"
                                                placeholder="New Password"
                                                value={newPassword}
                                                onChange={(e) => {
                                                    setNewPassword(e.target.value)
                                                    setErrorMessage('')
                                                }}
                                                onFocus={() => {
                                                    setPasswordRulesVisible(true)
                                                    // Smooth slide down animation for password rules
                                                    const $ = window.$
                                                    if ($) {
                                                        setTimeout(() => {
                                                            $('.password-rules-wrapper').slideDown(300)
                                                        }, 10)
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    // When tabbing away, check if password is valid before hiding
                                                    // Use setTimeout to allow focus to move to next field first
                                                    setTimeout(() => {
                                                        // Check if focus moved to confirm password or outside
                                                        const activeElement = document.activeElement
                                                        const isConfirmPasswordFocused = activeElement?.name === 'confirmPassword'
                                                        
                                                        // Only hide if password is valid or focus moved to confirm password
                                                        if (isPasswordValid(newPassword) || isConfirmPasswordFocused) {
                                                            const $ = window.$
                                                            if ($) {
                                                                $('.password-rules-wrapper').slideUp(300, () => {
                                                                    setPasswordRulesVisible(false)
                                                                })
                                                            } else {
                                                                setPasswordRulesVisible(false)
                                                            }
                                                        }
                                                    }, 100)
                                                }}
                                                required
                                                disabled={isLoading}
                                            />
                                            <div className="input-group-append">
                                                <div className="input-group-text">
                                                    <i className="fas fa-lock" />
                                                </div>
                                            </div>
                                        </div>
                                        {newPassword && passwordValidation && (
                                            <div
                                                ref={rulesRef}
                                                className="password-rules-wrapper mb-3"
                                                style={{ 
                                                    display: passwordRulesVisible ? 'block' : 'none',
                                                    fontSize: '0.85rem'
                                                }}
                                            >
                                                <strong className={passwordValidation.isValid ? 'text-success' : ''}>Password must contain:</strong>
                                                <ul className="mb-0" style={{ paddingLeft: '1.2rem', marginTop: '0.5rem' }}>
                                                    <li className={passwordValidation.errors.minLength ? 'text-danger' : 'text-success'}>
                                                        At least 8 characters
                                                    </li>
                                                    <li className={passwordValidation.errors.hasUpperCase ? 'text-danger' : 'text-success'}>
                                                        One uppercase letter
                                                    </li>
                                                    <li className={passwordValidation.errors.hasLowerCase ? 'text-danger' : 'text-success'}>
                                                        One lowercase letter
                                                    </li>
                                                    <li className={passwordValidation.errors.hasNumber ? 'text-danger' : 'text-success'}>
                                                        One number
                                                    </li>
                                                    <li className={passwordValidation.errors.hasSpecialChar ? 'text-danger' : 'text-success'}>
                                                        One special character
                                                    </li>
                                                </ul>
                                            </div>
                                        )}
                                        <div className="input-group mb-3">
                                            <input
                                                type="password"
                                                name="confirmPassword"
                                                className="form-control"
                                                placeholder="Confirm New Password"
                                                value={confirmPassword}
                                                onChange={(e) => {
                                                    setConfirmPassword(e.target.value)
                                                    setErrorMessage('')
                                                }}
                                                onFocus={() => {
                                                    // When clicking on confirm password, hide rules smoothly
                                                    const $ = window.$
                                                    if ($) {
                                                        $('.password-rules-wrapper').slideUp(300, () => {
                                                            setPasswordRulesVisible(false)
                                                        })
                                                    } else {
                                                        setPasswordRulesVisible(false)
                                                    }
                                                }}
                                                required
                                                disabled={isLoading}
                                            />
                                            <div className="input-group-append">
                                                <div className="input-group-text">
                                                    <i className="fas fa-lock" />
                                                </div>
                                            </div>
                                        </div>
                                        {confirmPassword && newPassword !== confirmPassword && (
                                            <div className="alert alert-danger mb-3" style={{ fontSize: '0.85rem', padding: '0.5rem' }}>
                                                Passwords do not match
                                            </div>
                                        )}
                                        {errorMessage && (
                                            <div className="alert alert-danger mb-3" role="alert">
                                                {errorMessage}
                                            </div>
                                        )}
                                        <div className="row">
                                            <div className="col-12 mt-3">
                                                <button
                                                    type="submit"
                                                    className="btn btn-blue btn-block"
                                                    disabled={isLoading || !temporaryPassword || !newPassword || !confirmPassword}
                                                >
                                                    {isLoading ? (
                                                        <PulseLoader color="#ffffff" size={8} />
                                                    ) : (
                                                        'Accept Invitation'
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </>
                            ) : (
                                <div className="text-center py-4">
                                    <div className="alert alert-danger" role="alert">
                                        {errorMessage || 'Invalid or expired invitation link. Please contact your administrator for a new invitation.'}
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-blue mt-3"
                                        onClick={() => navigate('/')}
                                    >
                                        Go to Login
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default UserRegister